/**
 * Ambient weather: IP-based location + keyless Open-Meteo current conditions.
 * Cached 30 min in localStorage; every failure degrades to `clear` — the
 * first paint never blocks on a fetch.
 */

const LOCATION_KEY = 'ada-ambient-location';
const WEATHER_KEY = 'ada-ambient-weather';
const WEATHER_TTL_MS = 30 * 60 * 1000;

/** Canonical condition set the scene + palette understand. */
export const WEATHER_IDS = [
  'clear',
  'clouds',
  'fog',
  'drizzle',
  'rain',
  'snow',
  'thunder',
];

/** WMO weather interpretation codes → canonical condition. */
export function wmoToCondition(code) {
  if (code == null) return 'clear';
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'clouds'; // mainly clear / partly cloudy
  if (code === 3) return 'clouds'; // overcast
  if (code >= 45 && code <= 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 61 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'rain'; // showers
  if (code >= 85 && code <= 86) return 'snow'; // snow showers
  if (code >= 95 && code <= 99) return 'thunder';
  return 'clear';
}

export const CONDITION_LABELS = {
  clear: 'Clear',
  clouds: 'Cloudy',
  fog: 'Fog',
  drizzle: 'Drizzle',
  rain: 'Rain',
  snow: 'Snow',
  thunder: 'Thunderstorms',
};

/** Factor (0..1) applied to accent chroma + glow strength. */
export const CONDITION_TINT = {
  clear: 1,
  clouds: 0.85,
  fog: 0.8,
  drizzle: 0.9,
  rain: 0.8,
  snow: 0.9,
  thunder: 0.7,
};

export function isWeatherId(id) {
  return WEATHER_IDS.includes(id);
}

function readCache(key, ttl) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (ttl && (!parsed.fetchedAt || Date.now() - parsed.fetchedAt > ttl)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / disabled — ambient weather is best-effort */
  }
}

/** Clear-cached location (no TTL; IP geolocation is stable enough). */
export function readCachedLocation() {
  return readCache(LOCATION_KEY, null);
}

export async function fetchLocation(signal) {
  const cached = readCachedLocation();
  if (cached) return cached;
  try {
    const res = await fetch('https://ipapi.co/json/', { signal });
    if (!res.ok) throw new Error(`ipapi ${res.status}`);
    const data = await res.json();
    const loc = {
      city: data.city || '',
      country: data.country_name || '',
      latitude: data.latitude,
      longitude: data.longitude,
      fetchedAt: Date.now(),
    };
    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
      writeCache(LOCATION_KEY, loc);
      return loc;
    }
  } catch {
    /* offline / blocked — fall through */
  }
  return null;
}

/**
 * Current weather via Open-Meteo (keyless). Returns:
 * { condition, tempC, isDay, code } or null when unavailable.
 */
export async function fetchWeather(latitude, longitude, signal) {
  const cacheKey = `${WEATHER_KEY}:${Number(latitude).toFixed(2)},${Number(longitude).toFixed(2)}`;
  const cached = readCache(cacheKey, WEATHER_TTL_MS);
  if (cached) return cached;
  const url =
    'https://api.open-meteo.com/v1/forecast' +
    `?latitude=${latitude}&longitude=${longitude}` +
    '&current=temperature_2m,weather_code,is_day' +
    '&daily=sunrise,sunset' +
    '&forecast_days=1&timezone=auto';
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`open-meteo ${res.status}`);
    const data = await res.json();
    const cur = data?.current;
    if (!cur || cur.weather_code == null) return null;
    const wx = {
      condition: wmoToCondition(cur.weather_code),
      tempC: typeof cur.temperature_2m === 'number' ? cur.temperature_2m : null,
      isDay: cur.is_day === 1,
      code: cur.weather_code,
      sunrise: data?.daily?.sunrise?.[0] || null,
      sunset: data?.daily?.sunset?.[0] || null,
      fetchedAt: Date.now(),
    };
    writeCache(cacheKey, wx);
    return wx;
  } catch {
    return null;
  }
}

export const CLEAR_WEATHER = Object.freeze({
  condition: 'clear',
  tempC: null,
  isDay: null,
  code: null,
  sunrise: null,
  sunset: null,
  fetchedAt: null,
});

/** Local hour as a float (e.g. 13.75 = 13:45). */
export function currentHourFloat(now = new Date()) {
  return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
}

/** Local hour in a named IANA zone (falls back to the device clock). */
export function hourInTimeZone(timeZone, now = new Date()) {
  if (!timeZone) return currentHourFloat(now);
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    const parts = Object.fromEntries(
      fmt.formatToParts(now).map((p) => [p.type, p.value])
    );
    return (
      Number(parts.hour) +
      Number(parts.minute) / 60 +
      Number(parts.second) / 3600
    );
  } catch {
    return currentHourFloat(now);
  }
}

/** Parse "2026-08-28T06:41" (Open-Meteo local ISO, no seconds) → hours. */
function localIsoToHours(iso) {
  if (!iso) return null;
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  return Number(m[1]) + Number(m[2]) / 60;
}

/**
 * Sun/moon arc position (0..1 across the visible sky) for the current hour.
 * Falls back to the static window schedule (dawn 6:30, dusk 19:30) so the
 * orb still moves when sunrise/sunset are unknown.
 */
export function celestialProgress(hourFloat, weather) {
  const rise = localIsoToHours(weather?.sunrise) ?? 6.5;
  const set = localIsoToHours(weather?.sunset) ?? 19.5;
  if (hourFloat >= rise && hourFloat < set) {
    return {
      body: 'sun',
      progress: (hourFloat - rise) / Math.max(0.1, set - rise),
    };
  }
  // Night wraps past midnight; measure from sunset → sunrise.
  const nightLen = (rise + 24 - set) % 24 || 12;
  const into = hourFloat >= set ? hourFloat - set : hourFloat + 24 - set;
  return { body: 'moon', progress: Math.min(1, into / nightLen) };
}

export function moonPhase(date = new Date()) {
  // Synodic month ≈ 29.53059 days from a known new moon (2000-01-06).
  const known = Date.UTC(2000, 0, 6, 18, 14);
  const days = (date.getTime() - known) / 86400000;
  return ((days % 29.53059) + 29.53059) % 29.53059 / 29.53059; // 0..1, 0=new
}
